import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '';

const poolData = {
  UserPoolId: userPoolId,
  ClientId: clientId,
};

const userPool = new CognitoUserPool(poolData);

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface CognitoUserData {
  email: string;
  sub: string;
  emailVerified: boolean;
  username: string;
  role: string;
  firstName: string;
  lastName: string;
}

/**
 * Sign up a new user with Cognito
 */
export const signUp = (
  email: string,
  password: string,
  attributes: { name: string; value: string }[] = [],
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const attributeList = attributes.map(
      (attr) => new CognitoUserAttribute({ Name: attr.name, Value: attr.value }),
    );

    // Add email attribute
    attributeList.push(new CognitoUserAttribute({ Name: 'email', Value: email }));

    userPool.signUp(email, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Confirm user registration with verification code
 */
export const confirmSignUp = (email: string, code: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Resend the sign-up confirmation code to the user's email
 */
export const resendSignUp = (email: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Sign in user with email and password
 */
export const signIn = (email: string, password: string): Promise<CognitoTokens> => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const tokens: CognitoTokens = {
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        };
        resolve(tokens);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = (): CognitoUser | null => {
  return userPool.getCurrentUser();
};

/**
 * Get current user session
 */
export const getCurrentSession = (): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user logged in'));
      return;
    }

    cognitoUser.getSession((err: any, session: CognitoUserSession) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session);
    });
  });
};

/**
 * Get user tokens from session
 */
export const getUserTokens = async (): Promise<CognitoTokens> => {
  const session = await getCurrentSession();

  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
};

/**
 * Get user data from ID token
 */
export const getUserData = async (): Promise<CognitoUserData> => {
  const session = await getCurrentSession();
  const idToken = session.getIdToken();

  return {
    email: idToken.payload.email,
    sub: idToken.payload.sub,
    emailVerified: idToken.payload.email_verified,
    username: idToken.payload['cognito:username'],
    role: idToken.payload['custom:role'] ?? '',
    firstName: idToken.payload.given_name ?? '',
    lastName: idToken.payload.family_name ?? '',
  };
};

/**
 * Sign out current user
 */
export const signOut = (): void => {
  const cognitoUser = getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

/**
 * Forgot password - send verification code
 */
export const forgotPassword = (email: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        resolve(data);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Confirm forgot password with code and new password
 */
export const confirmPassword = (email: string, code: string, newPassword: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve({ message: 'Password reset successful' });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Refresh user session
 */
export const refreshSession = (): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user logged in'));
      return;
    }

    cognitoUser.getSession((err: any, session: CognitoUserSession) => {
      if (err) {
        reject(err);
        return;
      }

      const refreshToken = session.getRefreshToken();

      cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) {
          reject(refreshErr);
          return;
        }
        resolve(newSession);
      });
    });
  });
};
